import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Avatar,
  CardMedia,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Divider
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import { getOffers } from '../services/offerService';
import brandService from '../services/brandService';
import { useAuth } from '../context/AuthContext';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArticleIcon from '@mui/icons-material/Article';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { NO_EXPIRY_DATE } from '../utils/offerDefaults';

const StyledCard = styled(Card)(({ theme, expired }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  opacity: expired ? 0.7 : 1,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const OfferCard = ({ offer }) => {
  const isExpired = new Date() > new Date(offer.expirationDate);
  
  return (
    <StyledCard expired={isExpired}>
      {offer.featuredImage && (
        <CardMedia
          component="img"
          height="200"
          image={offer.featuredImage}
          alt={offer.title}
        />
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LocalOfferIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
            {offer.title}
          </Typography>
          {isExpired && (
            <Chip 
              label="Expired" 
              color="error" 
              size="small"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {offer.description}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <ScheduleIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Expires: {dayjs(offer.expirationDate).format('MMM D, YYYY')}
          </Typography>
        </Box>
        
        {offer.brand && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {offer.brand.logo ? (
              <Avatar 
                src={offer.brand.logo} 
                sx={{ width: 20, height: 20, mr: 1 }}
                alt={offer.brand.name}
              />
            ) : (
              <Avatar sx={{ width: 20, height: 20, mr: 1, fontSize: 12 }}>
                {offer.brand.name?.charAt(0) || 'B'}
              </Avatar>
            )}
            <Typography 
              variant="body2" 
              color="primary.main"
              component={RouterLink}
              to={`/offers/by-brand/${offer.brand._id}`}
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {offer.brand.name}
            </Typography>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
            {offer.author?.name?.charAt(0) || 'A'}
          </Avatar>
          <Typography variant="body2" color="text.secondary">
            By {offer.author?.name || 'Anonymous'}
          </Typography>
        </Box>
      </CardContent>
      
      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          component="a"
          href={offer.purchaseUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          startIcon={<ShoppingCartIcon />}
          disabled={isExpired}
          sx={{ mr: 1 }}
        >
          Get Offer
        </Button>
        
        {offer.blogLink && (
          <Button
            component="a"
            href={offer.blogLink}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            startIcon={<ArticleIcon />}
            size="small"
          >
            Read More
          </Button>
        )}
        
        <Button
          component={RouterLink}
          to={`/offers/${offer.slug}`}
          variant="text"
          size="small"
          sx={{ ml: 'auto' }}
        >
          Details
        </Button>
      </CardActions>
    </StyledCard>
  );
};

const Offers = () => {
  const [offers, setOffers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [includeExpired, setIncludeExpired] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const { isAuthenticated } = useAuth();

  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [offerForm, setOfferForm] = useState({
    title: '',
    description: '',
    purchaseUrl: '',
    blogLink: '',
    expirationDate: NO_EXPIRY_DATE.toISOString().split('T')[0] // 7 days from now
  });
  const [submitLoading, setSubmitLoading] = useState(false);

  const fetchOffers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getOffers(page, 12, includeExpired, null, null, selectedBrand || null);
      setOffers(data.offers || []);
      setTotalPages(data.totalPages || 1);
      setError(null);
    } catch (err) {
      setError('Failed to fetch offers. Please try again later.');
      console.error('Error fetching offers:', err);
    } finally {
      setLoading(false);
    }
  }, [page, includeExpired, selectedBrand]);

  const fetchBrands = useCallback(async () => {
    try {
      setBrandsLoading(true);
      const response = await brandService.getAllBrands({ 
        page: 1, 
        limit: 100, 
        sortBy: 'name',
        sortOrder: 'asc'
      });
      setBrands(response.brands.filter(brand => brand.isActive));
    } catch (err) {
      console.error('Error fetching brands:', err);
      setBrands([]);
    } finally {
      setBrandsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBrandChange = (event) => {
    setSelectedBrand(event.target.value);
    setPage(1); // Reset to first page when filtering
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      // All offers tab
      setSelectedBrand('');
    }
    setPage(1);
  };

  const clearFilters = () => {
    setSelectedBrand('');
    setActiveTab(0);
    setPage(1);
  };

  const handleCreateOffer = () => {
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setOfferForm({
      title: '',
      description: '',
      purchaseUrl: '',
      blogLink: '',
      expirationDate: NO_EXPIRY_DATE.toISOString().split('T')[0]
    });
  };

  const handleFormChange = (field) => (event) => {
    setOfferForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmitOffer = async () => {
    try {
      setSubmitLoading(true);
      
      // Basic validation
      if (!offerForm.title || !offerForm.description || !offerForm.purchaseUrl) {
        setError('Please fill in all required fields');
        return;
      }

      // Create offer data
      const offerData = {
        ...offerForm,
        expirationDate: new Date(offerForm.expirationDate),
        published: true
      };

      // Import createOffer service
      const { createOffer } = await import('../services/offerService');
      await createOffer(offerData);
      
      setError(null);
      handleCloseDrawer();
      fetchOffers(); // Refresh the offers list
      
      // Show success message (you might want to add a success state)
      console.log('Offer created successfully!');
      
    } catch (err) {
      setError('Failed to create offer. Please try again.');
      console.error('Error creating offer:', err);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading && offers.length === 0) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  const offersActions = isAuthenticated ? (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button
        component={RouterLink}
        to="/offer-templates"
        variant="outlined"
      >
        Use Template
      </Button>
      <Button
        onClick={handleCreateOffer}
        variant="contained"
        startIcon={<LocalOfferIcon />}
      >
        Create Offer
      </Button>
    </Box>
  ) : null;

  return (
    <DashboardLayout title="Special Offers" actions={offersActions} maxWidth="lg">
      <Box sx={{ maxWidth: 'lg', mx: 'auto', px: 3, py: 2 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Discover exclusive deals and limited-time offers
          </Typography>
        
        {/* Tabs for different views */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="offer filter tabs">
            <Tab label="All Offers" />
            <Tab label="By Brand" />
            <Tab 
              label="By Category" 
              onClick={() => window.location.href = '/offers/by-category'}
            />
          </Tabs>
        </Box>

        {/* Brand Filter Section */}
        {activeTab === 1 && (
          <Box sx={{ mb: 3, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ mr: 1 }} />
              Filter by Brand
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Select Brand</InputLabel>
                <Select
                  value={selectedBrand}
                  onChange={handleBrandChange}
                  label="Select Brand"
                  disabled={brandsLoading}
                >
                  <MenuItem value="">
                    <em>All Brands</em>
                  </MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand._id} value={brand._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {brand.logo && (
                          <Avatar 
                            src={brand.logo} 
                            sx={{ width: 20, height: 20 }}
                            alt={brand.name}
                          />
                        )}
                        {brand.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {selectedBrand && (
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  size="small"
                >
                  Clear Filter
                </Button>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeExpired}
                  onChange={(e) => setIncludeExpired(e.target.checked)}
                  color="primary"
                />
              }
              label="Include expired offers"
            />
            {/* {activeTab === 0 && (
              <Button
                component={RouterLink}
                to="/offers/by-category"
                variant="outlined"
                startIcon={<LocalOfferIcon />}
              >
                Browse by Category
              </Button>
            )} */}
          </Box>
          

        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {offers.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <LocalOfferIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {selectedBrand ? 'No offers found for this brand' : 'No offers available at the moment'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedBrand ? 'Try selecting a different brand or clear the filter.' : 'Check back later for exciting deals!'}
          </Typography>
          {selectedBrand && (
            <Button
              variant="outlined"
              onClick={clearFilters}
              sx={{ mt: 2 }}
            >
              Show All Offers
            </Button>
          )}
        </Box>
      )}

      {/* Current filter status */}
      {selectedBrand && offers.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Showing offers from: <strong>{brands.find(b => b._id === selectedBrand)?.name}</strong>
            <Button
              size="small"
              onClick={clearFilters}
              sx={{ ml: 1 }}
            >
              Clear Filter
            </Button>
          </Typography>
        </Box>
      )}

      <Grid container spacing={3}>
        {offers.map((offer) => (
          <Grid item xs={12} sm={6} md={4} key={offer._id}>
            <OfferCard offer={offer} />
          </Grid>
        ))}
      </Grid>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            sx={{ mr: 2 }}
          >
            Previous
          </Button>
          <Typography sx={{ mx: 2, alignSelf: 'center' }}>
            Page {page} of {totalPages}
          </Typography>
          <Button
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            sx={{ ml: 2 }}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Create Offer Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 500 } }
        }}
      >
        <AppBar position="sticky" color="default" elevation={1}>
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Create New Offer
            </Typography>
            <IconButton onClick={handleCloseDrawer}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3 }}>
          <TextField
            fullWidth
            label="Offer Title"
            value={offerForm.title}
            onChange={handleFormChange('title')}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={offerForm.description}
            onChange={handleFormChange('description')}
            margin="normal"
            multiline
            rows={3}
            required
          />
          <TextField
            fullWidth
            label="Purchase URL"
            value={offerForm.purchaseUrl}
            onChange={handleFormChange('purchaseUrl')}
            margin="normal"
            required
            placeholder="https://..."
          />
          <TextField
            fullWidth
            label="Blog Link (Optional)"
            value={offerForm.blogLink}
            onChange={handleFormChange('blogLink')}
            margin="normal"
            placeholder="https://..."
          />
          <TextField
            fullWidth
            label="Expiration Date"
            type="date"
            value={offerForm.expirationDate}
            onChange={handleFormChange('expirationDate')}
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
          />
          
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              onClick={handleCloseDrawer}
              variant="contained"
              data-cancel="true"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitOffer}
              disabled={submitLoading}
              startIcon={submitLoading ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {submitLoading ? 'Creating...' : 'Create Offer'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
    </DashboardLayout>
  );
};

export default Offers; 